
import Vue from 'vue';
import { Prop, Component, Watch } from 'vue-property-decorator';
import PhotosModule from '@/store/modules/Photos';
import SessionModule from '@/store/modules/Sessions';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import Session from '@/model/Session';
import watermarkFilter from '@/service/watermarkFilter';
import Debounce from '@/decorators/debounce';
import Watermark from '@/model/Watermark';
import cloneDeep from 'lodash/cloneDeep';

const photosModule = getModule(PhotosModule, store);
const sessionModule = getModule(SessionModule, store);

@Component
export default class WatermarkDialog extends Vue {

    get confirmationDialog() {
        return this.confirmation &&
            this.value &&
            !this.confirmed;
    }

    get watermarkDialog() {
        return this.value && (
            this.confirmed ||
            !this.confirmation
        );
    }
    public initialState: any;
    public currentTab: 'text'|'graphics' = 'text';

    @Prop({
        type: Object,
        required: false,
        default: () => ({ enabled: false })
    })
    public currentWatermark!: Watermark;
    @Prop({
        type: Boolean,
        required: false,
        default: true,
    })
    public confirmation!: boolean;

    @Prop({
        type: Boolean,
        required: true,
    })
    public value!: boolean;

    public confirmed = false;

    public file: File|null = null;
    public opacity: number = 50;
    public size: number = 300;
    public spacing: number = 50;
    public text: string = '';
    public rendering = false;

    // Anti AI properties
    public antiAiEnabled: boolean = false;
    public antiAiDialog: boolean = false;
    private antiAiOpenedByToggle = false;
    public antiAiPreviewOpen: boolean = false;
    public antiAiPreviewUrl: string = '';
    public antiAiPreviewLoading: boolean = false;
    public antiAiPreviewError: boolean = false;
    private antiAiPreviewLevel: number | null = null;
    private antiAiPreviewRequest = {};
    public antiAiLevel: number = 1;
    public antiAiLevelSelected: boolean = false;
    public selectedOverlay: { id: number; name: string; preview: string } | null = null;
    public antiAiOverlays = [
        { id: 1, name: 'Poziom 1', preview: '/img/anti-ai/grid.png' },
        { id: 2, name: 'Poziom 2', preview: '/img/anti-ai/noise.png' },
        { id: 3, name: 'Poziom 3', preview: '/img/anti-ai/lines.png' }
    ];

    get hasWatermark() {
        return !!((this.currentTab === 'graphics' ? this.file : this.text.trim() && this.textFile) || this.currentWatermark?.image);
    }
    public exitRunBlink = false;
    private exitRunBlinkTimeout: number | null = null;

    private examplePhoto!: Uint8Array;
    private examplePhotoUrl!: string;
    private imageDiv!: HTMLDivElement;
    private textFile: Uint8Array|null = null;

    private watermark?: Promise<Watermark>;
    private watermarkRenderRequest = {};

    @Watch('value')
    public reset(val: boolean) {
        if (!val) {
            this.watermarkRenderRequest = {};
            this.clearAntiAiPreview();
            Object.assign(this.$data, cloneDeep(this.initialState));
            this.initialState = cloneDeep(this.$data);
        } else {
            const level = this.currentWatermark?.antiAiLevel;
            this.opacity = this.currentWatermark?.opacity || 50;
            this.size = this.currentWatermark?.size || 300;
            this.spacing = this.currentWatermark?.spacing || 50;
            this.antiAiEnabled = !!level;
            this.antiAiLevel = level || 1;
            this.antiAiLevelSelected = !!level;
            this.selectedOverlay = this.antiAiOverlays.find(overlay => overlay.id === level) || null;
            this.watermark = Promise.resolve(this.currentWatermark);
        }
    }

    @Watch("currentTab")
    public async onTabChange() {
        await this.renderWatermark();
    }

    public confirmationInput(value: boolean) {
        if (!value) {
            this.$emit('watermarkResolved', {
                enabled: false,
            });
            this.$emit('input', false);
        } else {
            this.confirmed = true;
        }
    }

    @Watch('size')
    @Watch('spacing')
    @Watch('opacity')
    @Watch('file')
    @Watch('textFile')
    @Debounce(300)
    public async generateWatermark() {
        await this.renderWatermark();
    }

    private async renderWatermark() {
        const request = this.watermarkRenderRequest = {};
        const file = this.currentTab === 'graphics' ? this.file : this.text.trim() ? this.textFile : null;
        const antiAiLevel = this.antiAiEnabled && this.antiAiLevelSelected && this.selectedOverlay ? this.antiAiLevel : undefined;
        const { opacity, size, spacing } = this;
        const currentWatermark = this.currentWatermark;
        const watermark = file || currentWatermark?.image ? (async (): Promise<Watermark> => ({
            enabled: file ? true : currentWatermark.enabled,
            opacity,
            size,
            spacing,
            image: file ? new Uint8Array(await new Response(file).arrayBuffer()) : currentWatermark.image,
            antiAiLevel,
        }))() : undefined;
        this.watermark = watermark;

        if (watermark) {
            this.rendering = true;
            try {
                if (!this.examplePhoto) await this.loadExamples();
                const preview = await watermarkFilter.generate(
                    this.examplePhoto,
                    (await watermark).image || null,
                    { opacity, size, spacing, antiAiLevel },
                );
                const reader = new FileReader();
                reader.readAsDataURL(new Blob([ preview ]));
                await new Promise((resolve) => reader.addEventListener('loadend', resolve));
                if (request !== this.watermarkRenderRequest) return;
                this.imageDiv.style.backgroundImage = `url("${reader.result}")`;
            } finally {
                if (request === this.watermarkRenderRequest) this.rendering = false;
            }
        } else {
            if (!this.examplePhoto) {
                await this.loadExamples();
            }
            if (request === this.watermarkRenderRequest) this.imageDiv.style.backgroundImage = `url("${this.examplePhotoUrl}")`;
        }
    }

    @Watch('text')
    @Debounce(250)
    public async renderText(value: string) {
        if (!value.trim()) {
            this.textFile = null;
            return;
        }
        const ctx = (this.$refs.textCanvas as HTMLCanvasElement).getContext('2d');
        if (ctx) {
            ctx.font = '300px Helvetica';
            const textSize = ctx.measureText(value);
            ctx.canvas.width = textSize.width;
            ctx.canvas.height = 450;

            ctx.fillStyle = '#ffffff';
            ctx.font = '300px Helvetica';
            ctx.fillText(value, 0, 300);
            const blob = await new Promise<Blob>((resolve) => ctx.canvas.toBlob((result) => {
                if (result) {
                    resolve(result);
                }
            }));
            this.textFile = new Uint8Array(await new Response(blob).arrayBuffer());
            const reader = new FileReader();
        }
    }

    public async acceptWatermark() {
        await this.renderWatermark();
        this.$emit('watermarkResolved', await this.watermark);
        this.$emit('input', false);
    }

    public async cancelWatermarkEdit() {
        if (this.confirmation) {
            this.confirmed = false;
        } else {
            this.$emit('input', false);
        }
    }

    // Anti AI methods
    public toggleAntiAi(enabled: boolean) {
        if (enabled) {
            this.antiAiOpenedByToggle = true;
            this.antiAiLevelSelected = !!this.selectedOverlay;
            this.antiAiLevel = this.selectedOverlay?.id || 1;
            this.antiAiDialog = true;
        } else {
            this.renderWatermark();
        }
    }

    public selectLevel(level: number) {
        const needsPreview = !this.antiAiLevelSelected || this.antiAiPreviewLevel !== level;
        this.antiAiLevel = level;
        this.antiAiLevelSelected = true;
        if (needsPreview) this.prepareAntiAiPreview();
    }

    public async removeAntiAi() {
        this.antiAiOpenedByToggle = false;
        this.antiAiEnabled = false;
        this.selectedOverlay = null;
        this.antiAiLevelSelected = false;
        this.antiAiLevel = 1;
        this.antiAiDialog = false;
        await this.renderWatermark();
    }

    @Watch('antiAiDialog')
    public onAntiAiDialogChange(open: boolean) {
        if (open && this.antiAiLevelSelected) this.prepareAntiAiPreview();
        if (!open) this.clearAntiAiPreview();
    }

    public selectOverlay(overlay: { id: number; name: string; preview: string }) {
        this.selectedOverlay = overlay;
        this.antiAiLevel = overlay.id;
    }

    public cancelAntiAi() {
        this.antiAiDialog = false;
        if (this.antiAiOpenedByToggle) {
            this.antiAiEnabled = false;
            this.renderWatermark();
        }
        this.antiAiOpenedByToggle = false;
        this.antiAiLevelSelected = !!this.selectedOverlay;
        this.antiAiLevel = this.selectedOverlay?.id || 1;
    }

    public async confirmAntiAi() {
        if (!this.antiAiLevelSelected) {
            return;
        }
        this.antiAiDialog = false;
        this.antiAiOpenedByToggle = false;
        // Automatically select the overlay based on the level
        this.selectedOverlay = this.antiAiOverlays.find(overlay => overlay.id === this.antiAiLevel) || null;
        await this.renderWatermark();
    }

    private async prepareAntiAiPreview() {
        const request = this.antiAiPreviewRequest = {};
        const level = this.antiAiLevel;
        this.antiAiPreviewLevel = level;
        if (this.antiAiPreviewUrl) URL.revokeObjectURL(this.antiAiPreviewUrl);
        this.antiAiPreviewUrl = '';
        this.antiAiPreviewError = false;
        this.antiAiPreviewLoading = true;
        try {
            if (!this.examplePhoto) await this.loadExamples();
            const file = this.currentTab === 'graphics' ? this.file : this.textFile;
            const image = file ? new Uint8Array(await new Response(file).arrayBuffer()) : this.currentWatermark?.image || null;
            const preview = await watermarkFilter.generate(this.examplePhoto, image, {
                opacity: this.opacity,
                size: this.size,
                spacing: this.spacing,
                antiAiLevel: level,
            });
            if (request !== this.antiAiPreviewRequest) return;
            this.antiAiPreviewUrl = URL.createObjectURL(new Blob([preview], { type: 'image/jpeg' }));
        } catch {
            if (request === this.antiAiPreviewRequest) this.antiAiPreviewError = true;
        } finally {
            if (request === this.antiAiPreviewRequest) this.antiAiPreviewLoading = false;
        }
    }

    public openAntiAiPreview() {
        if (this.antiAiPreviewError) return this.prepareAntiAiPreview();
        if (!this.antiAiPreviewUrl || this.antiAiPreviewLoading) return;
        this.antiAiPreviewOpen = true;
        window.addEventListener('keydown', this.closeAntiAiPreview);
        window.addEventListener('wheel', this.closeAntiAiPreview);
    }

    public closeAntiAiPreview() {
        window.removeEventListener('keydown', this.closeAntiAiPreview);
        window.removeEventListener('wheel', this.closeAntiAiPreview);
        this.antiAiPreviewOpen = false;
    }

    private clearAntiAiPreview() {
        this.antiAiPreviewRequest = {};
        this.antiAiPreviewLevel = null;
        this.closeAntiAiPreview();
        if (this.antiAiPreviewUrl) URL.revokeObjectURL(this.antiAiPreviewUrl);
        this.antiAiPreviewUrl = '';
        this.antiAiPreviewLoading = false;
        this.antiAiPreviewError = false;
    }

    public onDialogOutsideClick() {
        this.flashExitRun();
    }

    public mounted() {
        this.initialState = cloneDeep(this.$data);
        this.watermark = Promise.resolve<Watermark>(this.currentWatermark);
    }

    public beforeDestroy() {
        this.clearAntiAiPreview();
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }
    }

    private async loadExamples() {
        this.imageDiv = (
            (this.$refs.exampleImage as Vue).$el as HTMLDivElement
        ).querySelector('.v-image__image') as HTMLDivElement;
        const exampleUrl = this.imageDiv.style.backgroundImage!.match(/url\("?(.*?)"?\)/)![1];
        this.examplePhotoUrl = exampleUrl;
        this.examplePhoto = new Uint8Array(await (await fetch(exampleUrl)).arrayBuffer());
    }

    private flashExitRun() {
        this.exitRunBlink = false;
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }

        this.$nextTick(() => {
            this.exitRunBlink = true;
            this.exitRunBlinkTimeout = window.setTimeout(() => {
                this.exitRunBlink = false;
                this.exitRunBlinkTimeout = null;
            }, 900);
        });
    }
}
